import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { Link, useNavigate } from "react-router-dom";
import { Package, Calendar, ChevronRight, ClipboardList, ShoppingBag, X, CheckCircle, Star } from "lucide-react";

const Orders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/orders/history`, {
                headers: { Authorization: token },
            });

            if (!response.ok) throw new Error("Failed to fetch orders");

            const data = await response.json();
            const sortedOrders = data.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
            setOrders(sortedOrders);
            setLoading(false);
        } catch (err) {
            setError("ไม่สามารถดึงข้อมูลคำสั่งซื้อได้");
            setLoading(false);
        }
    };

    const getStatusBadge = (shippingStatus) => {
        const statusConfig = {
            PENDING_SHIPMENT: { color: "bg-yellow-100 text-yellow-700", icon: Package, text: "รอผู้ขายจัดส่ง" },
            SHIPPED: { color: "bg-green-100 text-green-700", icon: CheckCircle, text: "ซื้อสำเร็จ" },
        };
        const config = statusConfig[shippingStatus] || statusConfig.PENDING_SHIPMENT;
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <Icon size={14} />
                {config.text}
            </span>
        );
    };

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">กำลังโหลด...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-black rounded-lg">
                        <ClipboardList className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">ประวัติการสั่งซื้อ</h1>
                        <p className="text-gray-500">รายการคำสั่งซื้อทั้งหมดของคุณ</p>
                    </div>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                        {error}
                    </div>
                )}

                {orders.length === 0 && !error ? (
                    // Empty State
                    <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                        <Package className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">ไม่มีประวัติการสั่งซื้อ</h3>
                        <p className="text-gray-500 mb-8">คุณยังไม่มีคำสั่งซื้อใดๆ เริ่มช้อปเลย!</p>
                        <Link
                            to="/all-products"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all"
                        >
                            <ShoppingBag size={20} />
                            เลือกซื้อสินค้า
                        </Link>
                    </div>
                ) : (
                    // Orders List
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <div
                                key={order.orderId}
                                className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        {/* Product Image */}
                                        <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={order.productImage ? `${API_BASE_URL}${order.productImage}` : "https://via.placeholder.com/150?text=No+Image"}
                                                alt={order.productName || "สินค้า"}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=No+Image"; }}
                                            />
                                        </div>

                                        {/* Order Info */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm text-gray-500">คำสั่งซื้อ #{order.orderId}</span>
                                                {getStatusBadge(order.shippingStatus)}
                                            </div>
                                            <h4 className="font-semibold text-gray-900">{order.productName || "สินค้าไม่ได้ระบุชื่อ"}</h4>
                                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                                <Calendar size={14} />
                                                {new Date(order.orderDate).toLocaleDateString("th-TH", {
                                                    year: "numeric",
                                                    month: "long",
                                                    day: "numeric",
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Price & Action */}
                                    <div className="flex items-center gap-4 ml-24 md:ml-0">
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-red-600">
                                                ฿{order.totalAmount ? order.totalAmount.toLocaleString() : "0"}
                                            </p>
                                            <p className="text-sm text-gray-500">จำนวน {order.quantity} ชิ้น</p>
                                        </div>
                                        <button
                                            onClick={() => setSelectedOrder(order)}
                                            className="p-3 bg-gray-100 hover:bg-red-100 rounded-lg transition-colors"
                                        >
                                            <ChevronRight className="w-5 h-5 text-gray-600" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Order Details Modal */}
                {selectedOrder && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4">
                            {/* Backdrop */}
                            <div
                                className="fixed inset-0 bg-black/50"
                                onClick={() => setSelectedOrder(null)}
                            />

                            {/* Modal */}
                            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        รายละเอียดคำสั่งซื้อ #{selectedOrder.orderId}
                                    </h3>
                                    <button
                                        onClick={() => setSelectedOrder(null)}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* Order Details */}
                                <div className="space-y-4">
                                    <div className="flex gap-4">
                                        <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={selectedOrder.productImage ? `${API_BASE_URL}${selectedOrder.productImage}` : ""}
                                                alt={selectedOrder.productName}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{selectedOrder.productName}</h4>
                                            <p className="text-sm text-gray-500 mt-1">จำนวน: {selectedOrder.quantity} ชิ้น</p>
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-100 pt-4 space-y-3">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">วันที่สั่งซื้อ</span>
                                            <span className="text-gray-900">
                                                {new Date(selectedOrder.orderDate).toLocaleDateString("th-TH", {
                                                    year: "numeric",
                                                    month: "long",
                                                    day: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">สถานะ</span>
                                            {getStatusBadge(selectedOrder.shippingStatus)}
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-100 pt-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-semibold text-gray-900">ยอดสุทธิ</span>
                                            <span className="text-2xl font-bold text-red-600">
                                                ฿{selectedOrder.totalAmount?.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="w-full mt-6 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                >
                                    ปิด
                                </button>

                                {/* Review Button - only show if order is SHIPPED */}
                                {selectedOrder.shippingStatus === "SHIPPED" && (
                                    <button
                                        onClick={() => {
                                            setSelectedOrder(null);
                                            navigate(`/product-details/${selectedOrder.productId}`);
                                        }}
                                        className="w-full mt-3 py-3 px-4 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Star size={18} />
                                        ดูสินค้า & เขียนรีวิว
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Orders;
